import { Box, InputLabel, SxProps, TextField, Theme } from "@mui/material";
import type { ResponsiveStyleValue } from "@mui/system";
import customInputStyles from "global/components/CustomInput/CustomInput.styles";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import { handleWheelEvent } from "helpers/methods";
import { generateId } from "utils/accessibility";
import { useMemo } from "react";

interface CustomProps {
  autoFocus?: boolean;
  label?: any;
  placeHolder?: string;
  value?: string | number;
  onChange?: any;
  type?: string;
  name?: any;
  select?: boolean;
  onKeyPress?: any;
  onKeyDown?: any;
  InputProps?: any;
  error?: any;
  required?: boolean;
  InputLabelProps?: any;
  id?: string;
  sx?: SxProps<Theme>;
  propsToInputElement?: any;
  disabled?: boolean;
  onBlur?: Function;
  onClick?: Function;
  helperText?: string;
  inputType?: string;
  onPaste?: Function;
  multiline?: boolean;
  variant?: string;
  textAlign?: string;
  autoComplete?: string;
  size?: "small" | "medium";
  minRows?: number;
  maxRows?: number;
  /** Fixed row count for `multiline` fields — renders a plain, non-autosizing textarea. */
  rows?: number;
  labelSx?: SxProps<Theme>;
  widthNotRequired?: boolean;
  /**
   * Gap between the label and the field (MUI spacing units or a CSS length).
   * Takes a breakpoint map too, so a screen can tighten it on small viewports.
   */
  labelSpacing?: ResponsiveStyleValue<number | string>;
}

/**
 * Layers a caller's `sx` on top of the shared chrome instead of replacing it.
 *
 * The array form matters: `sx` may legally be an object, a callback or an
 * array, and spreading a callback into an object silently drops it — which is
 * how the shared defaults used to be lost the moment a screen passed anything
 * at all.
 */
const composeSx = (
  base: SxProps<Theme>,
  override?: SxProps<Theme>,
  extra?: SxProps<Theme>,
): SxProps<Theme> =>
  [
    base,
    ...(Array.isArray(override) ? override : [override]),
    ...(Array.isArray(extra) ? extra : [extra]),
  ] as SxProps<Theme>;

const CustomInput = (props: CustomProps) => {
  const classes = customInputStyles;
  const { error = null } = props;

  // Generate unique ID for accessibility
  const inputId = useMemo(() => props.id || generateId("input"), [props.id]);
  const labelId = useMemo(() => `${inputId}-label`, [inputId]);

  return (
    <Box
      width={props?.widthNotRequired ? "auto" : "100%"}
      sx={(theme) => ({
        borderBottom:
          props.variant === "standard"
            ? `1px solid ${theme.palette.divider}`
            : "none",
        pb: props.variant === "standard" ? 0.5 : 0,
      })}
    >
      {props.label && (
        <Box mb={props.labelSpacing ?? formFieldMetrics.labelGap}>
          <InputLabel
            id={labelId}
            htmlFor={inputId}
            sx={composeSx(classes.inputLabel, props.labelSx)}
            required={props.required}
          >
            {props.label}
          </InputLabel>
        </Box>
      )}
      <TextField
        autoFocus={props.autoFocus ?? false}
        sx={composeSx(classes.textField, props.sx, {
          "& .MuiInputBase-input": {
            textAlign: props?.textAlign ? props.textAlign : "left",
          },
        })}
        fullWidth
        size={props.size}
        multiline={props.multiline}
        rows={props.rows}
        minRows={props.rows ? undefined : props.minRows}
        maxRows={props.rows ? undefined : props.maxRows}
        variant={props.variant ? props.variant : "outlined"}
        id={inputId}
        placeholder={props.placeHolder}
        type={props.type}
        name={props.name}
        select={props.select}
        value={props.value}
        InputProps={props.InputProps}
        inputProps={props.propsToInputElement}
        onChange={props.onChange}
        onKeyPress={props.onKeyPress}
        onKeyDown={props.onKeyDown}
        onPaste={props.onPaste}
        required={props.required}
        onBlur={props.onBlur}
        onClick={props?.onClick}
        {...(error && { error: true, helperText: error })}
        disabled={props.disabled}
        helperText={props.helperText}
        onWheel={props.type === "number" ? handleWheelEvent : null}
        autoComplete={props.autoComplete ?? ""}
        // MUI wires the input's `aria-describedby` to its own
        // `${id}-helper-text`. Overriding the helper's `id` here would break
        // that link, so only augment it with a live-region role for errors.
        FormHelperTextProps={{
          role: error ? "alert" : undefined,
        }}
      />
    </Box>
  );
};

export default CustomInput;
